// Admin Panel Zoom Integration - Implementare completă
import React, { useState, useEffect } from 'react';
import {
  Video,
  Calendar,
  Users,
  Clock,
  Settings,
  CheckCircle2,
  AlertCircle,
  RefreshCw,
  Play,
  Pause,
  Square,
  Mic,
  MicOff,
  Camera,
  CameraOff,
  Share,
  Phone,
  PhoneOff,
  Monitor,
  MessageSquare,
  Circle,
  Download,
  Upload,
  Link,
  Copy,
  Edit,
  Trash2,
  Plus,
  Eye,
  EyeOff,
  Globe,
  Shield,
  Zap,
  Activity,
  Bell,
  User,
  UserPlus,
  Mail,
  ExternalLink,
  FileText,
  BarChart3
} from 'lucide-react';

const ZoomIntegration = () => {
  const [activeTab, setActiveTab] = useState('overview');
  const [isConnecting, setIsConnecting] = useState(false);
  const [connectionStatus, setConnectionStatus] = useState('connected');
  const [showApiKey, setShowApiKey] = useState(false);
  const [showApiSecret, setShowApiSecret] = useState(false);

  // Mock statistics
  const [stats] = useState({
    totalMeetings: 147,
    scheduledMeetings: 23,
    activeMeetings: 3,
    completedMeetings: 121,
    totalParticipants: 1256,
    averageParticipants: 8.5,
    meetingDuration: '45 min',
    recordedMeetings: 89,
    totalRecordingSize: '15.7 GB',
    lastSync: '2025-01-15 16:45'
  });

  // Mock upcoming meetings
  const [upcomingMeetings] = useState([
    {
      id: '123456789',
      title: 'Prezentare Proiect Q1',
      startTime: '2025-01-16 10:00',
      duration: 60,
      host: 'Maria Popescu',
      participants: 12,
      status: 'scheduled',
      hasPassword: true,
      waitingRoom: true,
      recordMeeting: true
    },
    {
      id: '987654321',
      title: 'Team Standup Daily',
      startTime: '2025-01-16 09:30',
      duration: 30,
      host: 'Ion Ionescu',
      participants: 8,
      status: 'scheduled',
      hasPassword: false,
      waitingRoom: false,
      recordMeeting: false
    },
    {
      id: '456789123',
      title: 'Client Review - Casa Cluj',
      startTime: '2025-01-16 14:00',
      duration: 90,
      host: 'Admin Sistem',
      participants: 5,
      status: 'scheduled',
      hasPassword: true,
      waitingRoom: true,
      recordMeeting: true
    }
  ]);

  // Mock recent meetings
  const [recentMeetings] = useState([
    {
      id: '111222333',
      title: 'Weekly Planning Meeting',
      startTime: '2025-01-15 15:00',
      endTime: '2025-01-15 16:30',
      duration: 90,
      host: 'Maria Popescu',
      participants: 15,
      status: 'completed',
      recorded: true,
      recordingSize: '245 MB'
    },
    {
      id: '444555666',
      title: 'Training Session - New Tools',
      startTime: '2025-01-15 11:00',
      endTime: '2025-01-15 12:00',
      duration: 60,
      host: 'Ana Marinescu',
      participants: 22,
      status: 'completed',
      recorded: true,
      recordingSize: '180 MB'
    },
    {
      id: '777888999',
      title: 'Client Consultation',
      startTime: '2025-01-15 09:00',
      endTime: '2025-01-15 09:45',
      duration: 45,
      host: 'Ion Ionescu',
      participants: 3,
      status: 'completed',
      recorded: false,
      recordingSize: null
    }
  ]);

  // Mock settings
  const [settings, setSettings] = useState({
    apiKey: 'your_zoom_api_key_here_12345',
    apiSecret: 'your_zoom_api_secret_here_67890',
    webhookUrl: 'https://botez-de-poveste.ro/webhooks/zoom',
    autoRecord: true,
    waitingRoom: true,
    meetingPassword: true,
    allowJoinBeforeHost: false,
    muteOnEntry: true,
    enableChat: true,
    enableScreenShare: true,
    syncCalendar: true,
    emailNotifications: true
  });

  const handleConnect = async () => {
    setIsConnecting(true);
    // Simulate connection process
    setTimeout(() => {
      setIsConnecting(false);
      setConnectionStatus('connected');
    }, 2000);
  };

  const handleSettingChange = (key, value) => {
    setSettings(prev => ({
      ...prev,
      [key]: value
    }));
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'connected':
        return (
          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300">
            <CheckCircle2 className="w-3 h-3 mr-1" />
            Conectat
          </span>
        );
      case 'connecting':
        return (
          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300">
            <RefreshCw className="w-3 h-3 mr-1 animate-spin" />
            Se conectează
          </span>
        );
      case 'error':
        return (
          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300">
            <AlertCircle className="w-3 h-3 mr-1" />
            Eroare
          </span>
        );
      default:
        return (
          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-300">
            <Clock className="w-3 h-3 mr-1" />
            Deconectat
          </span>
        );
    }
  };

  const getMeetingStatusColor = (status) => {
    switch (status) {
      case 'scheduled': return 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300';
      case 'live': return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300';
      case 'completed': return 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-300';
      case 'cancelled': return 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300';
      default: return 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-300';
    }
  };

  const formatDuration = (minutes) => {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return hours > 0 ? `${hours}h ${mins}m` : `${mins}m`;
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <Video className="w-8 h-8 text-blue-600 mr-3" />
              <div>
                <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
                  Zoom Integration
                </h1>
                <p className="text-gray-600 dark:text-gray-400 mt-1">
                  Gestionează meeting-urile și integrarea cu Zoom
                </p>
              </div>
            </div>
            <div className="flex items-center space-x-3">
              <button 
                onClick={handleConnect}
                disabled={isConnecting}
                className="px-4 py-2 border border-blue-300 text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/20 font-medium rounded-lg transition-colors flex items-center"
              >
                {isConnecting ? (
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <RefreshCw className="w-4 h-4 mr-2" />
                )}
                {isConnecting ? 'Se conectează...' : 'Test Conexiune'}
              </button>
              <button className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-lg transition-colors flex items-center">
                <Plus className="w-4 h-4 mr-2" />
                Nou Meeting
              </button>
            </div>
          </div>
        </div>

        {/* Connection Status Banner */}
        <div className="bg-gradient-to-r from-blue-50 to-purple-50 dark:from-blue-900/20 dark:to-purple-900/20 rounded-xl border border-blue-200 dark:border-blue-800 p-6 mb-8">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <Video className="w-6 h-6 text-blue-600 mr-3" />
              <div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                  Status Conexiune Zoom
                </h3>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  API conectat • Ultima sincronizare: {stats.lastSync}
                </p>
              </div>
            </div>
            {getStatusBadge(connectionStatus)}
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4 mb-8">
          <div className="bg-white dark:bg-gray-800 p-4 rounded-xl shadow-soft border border-gray-200 dark:border-gray-700">
            <div className="flex items-center">
              <Video className="w-8 h-8 text-blue-600 mr-3" />
              <div>
                <p className="text-2xl font-bold text-gray-900 dark:text-white">{stats.totalMeetings}</p>
                <p className="text-xs text-gray-500">Total meetings</p>
              </div>
            </div>
          </div>
          
          <div className="bg-white dark:bg-gray-800 p-4 rounded-xl shadow-soft border border-gray-200 dark:border-gray-700">
            <div className="flex items-center">
              <Calendar className="w-8 h-8 text-green-600 mr-3" />
              <div>
                <p className="text-2xl font-bold text-gray-900 dark:text-white">{stats.scheduledMeetings}</p>
                <p className="text-xs text-gray-500">Programate</p>
              </div>
            </div>
          </div>

          <div className="bg-white dark:bg-gray-800 p-4 rounded-xl shadow-soft border border-gray-200 dark:border-gray-700">
            <div className="flex items-center">
              <div className="w-8 h-8 bg-red-100 dark:bg-red-900/20 rounded-lg flex items-center justify-center mr-3">
                <div className="w-2 h-2 bg-red-500 rounded-full animate-pulse"></div>
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-900 dark:text-white">{stats.activeMeetings}</p>
                <p className="text-xs text-gray-500">Live acum</p>
              </div>
            </div>
          </div>

          <div className="bg-white dark:bg-gray-800 p-4 rounded-xl shadow-soft border border-gray-200 dark:border-gray-700">
            <div className="flex items-center">
              <Users className="w-8 h-8 text-purple-600 mr-3" />
              <div>
                <p className="text-2xl font-bold text-gray-900 dark:text-white">{stats.totalParticipants.toLocaleString()}</p>
                <p className="text-xs text-gray-500">Participanți</p>
              </div>
            </div>
          </div>

          <div className="bg-white dark:bg-gray-800 p-4 rounded-xl shadow-soft border border-gray-200 dark:border-gray-700">
            <div className="flex items-center">
              <Circle className="w-8 h-8 text-orange-600 mr-3" />
              <div>
                <p className="text-2xl font-bold text-gray-900 dark:text-white">{stats.recordedMeetings}</p>
                <p className="text-xs text-gray-500">Înregistrări</p>
              </div>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="mb-8">
          <nav className="flex space-x-8">
            {[
              { id: 'overview', name: 'Prezentare Generală', icon: Activity },
              { id: 'meetings', name: 'Meeting-uri', icon: Video },
              { id: 'recordings', name: 'Înregistrări', icon: Circle },
              { id: 'settings', name: 'Setări', icon: Settings }
            ].map((tab) => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center px-3 py-2 rounded-lg font-medium transition-colors ${
                    activeTab === tab.id
                      ? 'bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300'
                      : 'text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200'
                  }`}
                >
                  <Icon className="w-4 h-4 mr-2" />
                  {tab.name}
                </button>
              );
            })}
          </nav>
        </div>

        {/* Tab Content */}
        {activeTab === 'overview' && (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            
            {/* Upcoming Meetings */}
            <div className="lg:col-span-2">
              <div className="bg-white dark:bg-gray-800 rounded-xl shadow-soft border border-gray-200 dark:border-gray-700 p-6">
                <div className="flex items-center justify-between mb-6">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                    Meeting-uri Programate
                  </h3>
                  <button className="text-sm text-blue-600 hover:text-blue-700 font-medium">
                    Vezi toate
                  </button>
                </div>
                
                <div className="space-y-4">
                  {upcomingMeetings.map((meeting) => (
                    <div key={meeting.id} className="border border-gray-200 dark:border-gray-600 rounded-lg p-4">
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center">
                          <Video className="w-5 h-5 text-blue-600 mr-3" />
                          <div>
                            <h4 className="font-semibold text-gray-900 dark:text-white">
                              {meeting.title}
                            </h4>
                            <p className="text-sm text-gray-500">
                              {meeting.startTime} • {formatDuration(meeting.duration)} • {meeting.host}
                            </p>
                          </div>
                        </div>
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${getMeetingStatusColor(meeting.status)}`}>
                          {meeting.status}
                        </span>
                      </div>

                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-4 text-sm text-gray-500">
                          <span className="flex items-center">
                            <Users className="w-4 h-4 mr-1" />
                            {meeting.participants} participanți
                          </span>
                          {meeting.hasPassword && (
                            <span className="flex items-center">
                              <Shield className="w-4 h-4 mr-1" />
                              Cu parolă
                            </span>
                          )}
                          {meeting.recordMeeting && (
                            <span className="flex items-center">
                              <Circle className="w-4 h-4 mr-1" />
                              Se înregistrează
                            </span>
                          )}
                        </div>
                        <div className="flex space-x-2">
                          <button className="px-3 py-1 text-xs bg-blue-600 hover:bg-blue-700 text-white rounded transition-colors">
                            Participă
                          </button>
                          <button className="px-3 py-1 text-xs border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 rounded transition-colors">
                            Editează
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Quick Stats & Actions */}
            <div className="space-y-6">
              
              {/* Quick Actions */}
              <div className="bg-white dark:bg-gray-800 rounded-xl shadow-soft border border-gray-200 dark:border-gray-700 p-6">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                  Acțiuni Rapide
                </h3>
                <div className="space-y-3">
                  <button className="w-full flex items-center p-3 bg-blue-50 dark:bg-blue-900/20 hover:bg-blue-100 dark:hover:bg-blue-900/30 text-blue-700 dark:text-blue-300 rounded-lg transition-colors">
                    <Plus className="w-5 h-5 mr-3" />
                    Creează Meeting Instant
                  </button>
                  <button className="w-full flex items-center p-3 bg-green-50 dark:bg-green-900/20 hover:bg-green-100 dark:hover:bg-green-900/30 text-green-700 dark:text-green-300 rounded-lg transition-colors">
                    <Calendar className="w-5 h-5 mr-3" />
                    Programează Meeting
                  </button>
                  <button className="w-full flex items-center p-3 bg-purple-50 dark:bg-purple-900/20 hover:bg-purple-100 dark:hover:bg-purple-900/30 text-purple-700 dark:text-purple-300 rounded-lg transition-colors">
                    <Circle className="w-5 h-5 mr-3" />
                    Vezi Înregistrări
                  </button>
                  <button className="w-full flex items-center p-3 bg-orange-50 dark:bg-orange-900/20 hover:bg-orange-100 dark:hover:bg-orange-900/30 text-orange-700 dark:text-orange-300 rounded-lg transition-colors">
                    <BarChart3 className="w-5 h-5 mr-3" />
                    Rapoarte Utilizare
                  </button>
                </div>
              </div>

              {/* Meeting Stats */}
              <div className="bg-white dark:bg-gray-800 rounded-xl shadow-soft border border-gray-200 dark:border-gray-700 p-6">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                  Statistici
                </h3>
                <div className="space-y-4">
                  <div className="flex justify-between">
                    <span className="text-gray-600 dark:text-gray-400">Durată medie:</span>
                    <span className="font-medium text-gray-900 dark:text-white">{stats.meetingDuration}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600 dark:text-gray-400">Participanți mediu:</span>
                    <span className="font-medium text-gray-900 dark:text-white">{stats.averageParticipants}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600 dark:text-gray-400">Spațiu înregistrări:</span>
                    <span className="font-medium text-gray-900 dark:text-white">{stats.totalRecordingSize}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600 dark:text-gray-400">Rata de înregistrare:</span>
                    <span className="font-medium text-gray-900 dark:text-white">
                      {Math.round((stats.recordedMeetings / stats.totalMeetings) * 100)}%
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'meetings' && (
          <div className="space-y-8">
            
            {/* Meetings List */}
            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-soft border border-gray-200 dark:border-gray-700 p-6">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                  Toate Meeting-urile
                </h3>
                <button className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-lg transition-colors flex items-center">
                  <Plus className="w-4 h-4 mr-2" />
                  Nou Meeting
                </button>
              </div>

              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-gray-200 dark:border-gray-700">
                      <th className="text-left py-3 px-4 font-medium text-gray-900 dark:text-white">Meeting</th>
                      <th className="text-left py-3 px-4 font-medium text-gray-900 dark:text-white">Data/Ora</th>
                      <th className="text-left py-3 px-4 font-medium text-gray-900 dark:text-white">Host</th>
                      <th className="text-left py-3 px-4 font-medium text-gray-900 dark:text-white">Participanți</th>
                      <th className="text-left py-3 px-4 font-medium text-gray-900 dark:text-white">Status</th>
                      <th className="text-left py-3 px-4 font-medium text-gray-900 dark:text-white">Acțiuni</th>
                    </tr>
                  </thead>
                  <tbody>
                    {[...upcomingMeetings, ...recentMeetings.slice(0, 2)].map((meeting, index) => (
                      <tr key={meeting.id} className="border-b border-gray-100 dark:border-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700">
                        <td className="py-3 px-4">
                          <div className="flex items-center">
                            <Video className="w-5 h-5 text-blue-600 mr-3" />
                            <div>
                              <span className="font-medium text-gray-900 dark:text-white">{meeting.title}</span>
                              <p className="text-sm text-gray-500">ID: {meeting.id}</p>
                            </div>
                          </div>
                        </td>
                        <td className="py-3 px-4 text-gray-600 dark:text-gray-400">
                          {meeting.startTime}
                          {meeting.endTime && (
                            <p className="text-sm text-gray-500">până la {meeting.endTime}</p>
                          )}
                        </td>
                        <td className="py-3 px-4 text-gray-600 dark:text-gray-400">{meeting.host}</td>
                        <td className="py-3 px-4 text-gray-600 dark:text-gray-400">{meeting.participants}</td>
                        <td className="py-3 px-4">
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${getMeetingStatusColor(meeting.status)}`}>
                            {meeting.status}
                          </span>
                        </td>
                        <td className="py-3 px-4">
                          <div className="flex space-x-2">
                            {meeting.status === 'scheduled' && (
                              <>
                                <button className="p-1 text-green-600 hover:text-green-700 rounded">
                                  <Play className="w-4 h-4" />
                                </button>
                                <button className="p-1 text-blue-600 hover:text-blue-700 rounded">
                                  <Edit className="w-4 h-4" />
                                </button>
                              </>
                            )}
                            {meeting.status === 'completed' && meeting.recorded && (
                              <button className="p-1 text-purple-600 hover:text-purple-700 rounded">
                                <Download className="w-4 h-4" />
                              </button>
                            )}
                            <button className="p-1 text-gray-400 hover:text-blue-600 rounded">
                              <Copy className="w-4 h-4" />
                            </button>
                            <button className="p-1 text-gray-400 hover:text-red-600 rounded">
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'recordings' && (
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-soft border border-gray-200 dark:border-gray-700 p-6">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                Înregistrări Meeting-uri
              </h3>
              <div className="flex items-center space-x-3">
                <span className="text-sm text-gray-600 dark:text-gray-400">
                  Total: {stats.totalRecordingSize}
                </span>
                <button className="px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 font-medium rounded-lg transition-colors flex items-center">
                  <Download className="w-4 h-4 mr-2" />
                  Descarcă Toate
                </button>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {recentMeetings.filter(m => m.recorded).map((meeting) => (
                <div key={meeting.id} className="border border-gray-200 dark:border-gray-600 rounded-lg p-4">
                <div className="flex items-center justify-between mb-3">
                <Circle className="w-6 h-6 text-red-600" />
                <span className="text-xs text-gray-500">{meeting.recordingSize}</span>
                </div>
                  
                  <h4 className="font-semibold text-gray-900 dark:text-white mb-2">
                    {meeting.title}
                  </h4>
                  
                  <div className="space-y-2 text-sm text-gray-600 dark:text-gray-400 mb-4">
                    <p>Data: {meeting.startTime}</p>
                    <p>Durată: {formatDuration(meeting.duration)}</p>
                    <p>Host: {meeting.host}</p>
                    <p>Participanți: {meeting.participants}</p>
                  </div>

                  <div className="flex space-x-2">
                    <button className="flex-1 px-3 py-2 text-sm bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors flex items-center justify-center">
                      <Play className="w-4 h-4 mr-1" />
                      Play
                    </button>
                    <button className="px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 rounded-lg transition-colors">
                      <Download className="w-4 h-4" />
                    </button>
                    <button className="px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 rounded-lg transition-colors">
                      <Share className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {activeTab === 'settings' && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            
            {/* API Settings */}
            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-soft border border-gray-200 dark:border-gray-700 p-6">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-6">
                Configurare API Zoom
              </h3>
              
              <div className="space-y-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    API Key
                  </label>
                  <div className="relative">
                    <input
                      type={showApiKey ? 'text' : 'password'}
                      value={settings.apiKey}
                      onChange={(e) => handleSettingChange('apiKey', e.target.value)}
                      className="w-full px-3 py-2 pr-10 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    />
                    <button
                      type="button"
                      onClick={() => setShowApiKey(!showApiKey)}
                      className="absolute inset-y-0 right-0 pr-3 flex items-center"
                    >
                      {showApiKey ? (
                        <EyeOff className="h-4 w-4 text-gray-400" />
                      ) : (
                        <Eye className="h-4 w-4 text-gray-400" />
                      )}
                    </button>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    API Secret
                  </label>
                  <div className="relative">
                    <input
                      type={showApiSecret ? 'text' : 'password'}
                      value={settings.apiSecret}
                      onChange={(e) => handleSettingChange('apiSecret', e.target.value)}
                      className="w-full px-3 py-2 pr-10 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    />
                    <button
                      type="button"
                      onClick={() => setShowApiSecret(!showApiSecret)}
                      className="absolute inset-y-0 right-0 pr-3 flex items-center"
                    >
                      {showApiSecret ? (
                        <EyeOff className="h-4 w-4 text-gray-400" />
                      ) : (
                        <Eye className="h-4 w-4 text-gray-400" />
                      )}
                    </button>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Webhook URL
                  </label>
                  <div className="flex">
                    <input
                      type="url"
                      value={settings.webhookUrl}
                      onChange={(e) => handleSettingChange('webhookUrl', e.target.value)}
                      className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-l-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    />
                    <button
                      onClick={() => copyToClipboard(settings.webhookUrl)}
                      className="px-3 py-2 border border-l-0 border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-600 text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-500 rounded-r-lg transition-colors"
                    >
                      <Copy className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
            </div>

            {/* Meeting Settings */}
            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-soft border border-gray-200 dark:border-gray-700 p-6">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-6">
                Setări Meeting Default
              </h3>
              
              <div className="space-y-6">
                <div className="grid grid-cols-1 gap-4">
                  <div>
                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        checked={settings.autoRecord}
                        onChange={(e) => handleSettingChange('autoRecord', e.target.checked)}
                        className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 dark:focus:ring-blue-600 dark:ring-offset-gray-800 focus:ring-2 dark:bg-gray-700 dark:border-gray-600"
                      />
                      <span className="ml-2 text-sm text-gray-900 dark:text-white">
                        Înregistrează automat meeting-urile
                      </span>
                    </label>
                  </div>

                  <div>
                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        checked={settings.waitingRoom}
                        onChange={(e) => handleSettingChange('waitingRoom', e.target.checked)}
                        className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 dark:focus:ring-blue-600 dark:ring-offset-gray-800 focus:ring-2 dark:bg-gray-700 dark:border-gray-600"
                      />
                      <span className="ml-2 text-sm text-gray-900 dark:text-white">
                        Activează waiting room
                      </span>
                    </label>
                  </div>

                  <div>
                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        checked={settings.meetingPassword}
                        onChange={(e) => handleSettingChange('meetingPassword', e.target.checked)}
                        className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 dark:focus:ring-blue-600 dark:ring-offset-gray-800 focus:ring-2 dark:bg-gray-700 dark:border-gray-600"
                      />
                      <span className="ml-2 text-sm text-gray-900 dark:text-white">
                        Necesită parolă pentru meeting-uri
                      </span>
                    </label>
                  </div>

                  <div>
                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        checked={settings.muteOnEntry}
                        onChange={(e) => handleSettingChange('muteOnEntry', e.target.checked)}
                        className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 dark:focus:ring-blue-600 dark:ring-offset-gray-800 focus:ring-2 dark:bg-gray-700 dark:border-gray-600"
                      />
                      <span className="ml-2 text-sm text-gray-900 dark:text-white">
                        Mute participanții la intrare
                      </span>
                    </label>
                  </div>

                  <div>
                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        checked={settings.enableChat}
                        onChange={(e) => handleSettingChange('enableChat', e.target.checked)}
                        className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 dark:focus:ring-blue-600 dark:ring-offset-gray-800 focus:ring-2 dark:bg-gray-700 dark:border-gray-600"
                      />
                      <span className="ml-2 text-sm text-gray-900 dark:text-white">
                        Permite chat în meeting
                      </span>
                    </label>
                  </div>

                  <div>
                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        checked={settings.enableScreenShare}
                        onChange={(e) => handleSettingChange('enableScreenShare', e.target.checked)}
                        className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 dark:focus:ring-blue-600 dark:ring-offset-gray-800 focus:ring-2 dark:bg-gray-700 dark:border-gray-600"
                      />
                      <span className="ml-2 text-sm text-gray-900 dark:text-white">
                        Permite screen sharing
                      </span>
                    </label>
                  </div>

                  <div>
                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        checked={settings.syncCalendar}
                        onChange={(e) => handleSettingChange('syncCalendar', e.target.checked)}
                        className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 dark:focus:ring-blue-600 dark:ring-offset-gray-800 focus:ring-2 dark:bg-gray-700 dark:border-gray-600"
                      />
                      <span className="ml-2 text-sm text-gray-900 dark:text-white">
                        Sincronizare cu calendar
                      </span>
                    </label>
                  </div>

                  <div>
                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        checked={settings.emailNotifications}
                        onChange={(e) => handleSettingChange('emailNotifications', e.target.checked)}
                        className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 dark:focus:ring-blue-600 dark:ring-offset-gray-800 focus:ring-2 dark:bg-gray-700 dark:border-gray-600"
                      />
                      <span className="ml-2 text-sm text-gray-900 dark:text-white">
                        Notificări email
                      </span>
                    </label>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ZoomIntegration;